import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart, HeatmapChart, LineChart } from 'echarts/charts';
import {
    GridComponent,
    LegendComponent,
    TooltipComponent,
    VisualMapComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { format, parseISO } from 'date-fns';

echarts.use([
    GridComponent,
    LegendComponent,
    TooltipComponent,
    VisualMapComponent,
    BarChart,
    HeatmapChart,
    LineChart,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

type Period = 'daily' | 'weekly' | 'monthly' | 'quarterly';

interface TrendBucket {
    period?: string;
    period_start?: string;
    total_sessions?: number;
    engaged_sessions?: number;
    low_engagement_sessions?: number;
}

interface LandingPageQualityItem {
    page_path?: string;
    page_label?: string;
    sessions?: number;
    engaged_sessions?: number;
    low_engagement_sessions?: number;
    engagement_rate_percent?: number;
}

interface TrafficSourceQualityItem {
    source_label?: string;
    sessions?: number;
    engaged_sessions?: number;
    low_engagement_sessions?: number;
    engagement_rate_percent?: number;
}

interface DurationBucketItem {
    duration_bucket?: string;
    sessions?: number;
    engaged_sessions?: number;
    low_engagement_sessions?: number;
    engagement_rate_percent?: number;
}

interface HeatmapItem {
    day_of_week?: number;
    hour_of_day?: number;
    sessions?: number;
    engaged_sessions?: number;
    engagement_rate_percent?: number;
}

const formatPeriodLabel = (date: Date, period: Period, fallback: string) => {
    switch (period) {
        case 'daily':
            return format(date, 'MMM d');
        case 'weekly':
            return `W${format(date, 'II')} ${format(date, 'yyyy')}`;
        case 'monthly':
            return format(date, 'MMM yyyy');
        case 'quarterly':
            return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
        default:
            return fallback;
    }
};

const parsePeriodDate = (value?: string) => {
    if (!value) {
        return new Date();
    }
    const parsed = parseISO(value);
    return isNaN(parsed.getTime()) ? new Date(value) : parsed;
};

const compactLabel = (value?: string, maxLength = 28) => {
    const label = value || 'Unknown';
    return label.length > maxLength ? `${label.slice(0, maxLength - 1)}...` : label;
};

const ChartRenderer: React.FC<{ option: any; height?: number }> = ({ option, height = 320 }) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!chartRef.current) {
            return undefined;
        }

        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        chartInstance.current.setOption(option, true);

        const resizeHandler = () => chartInstance.current?.resize();
        window.addEventListener('resize', resizeHandler);
        return () => {
            window.removeEventListener('resize', resizeHandler);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, [option]);

    return <div ref={chartRef} style={{ width: '100%', height }} />;
};

export const EngagementRateTrendChart: React.FC<{ data?: TrendBucket[]; period: Period }> = ({ data = [], period }) => {
    const option = React.useMemo(() => {
        const categories: string[] = [];
        const engagedRates: number[] = [];
        const lowRates: number[] = [];
        const totals: number[] = [];
        const engagedCounts: number[] = [];
        const lowCounts: number[] = [];

        data.forEach(item => {
            const engaged = Number(item.engaged_sessions) || 0;
            const low = Number(item.low_engagement_sessions) || 0;
            const total = Number(item.total_sessions) || engaged + low;

            categories.push(formatPeriodLabel(parsePeriodDate(item.period_start), period, item.period || ''));
            engagedCounts.push(engaged);
            lowCounts.push(low);
            totals.push(total);
            engagedRates.push(total > 0 ? Number(((engaged / total) * 100).toFixed(1)) : 0);
            lowRates.push(total > 0 ? Number(((low / total) * 100).toFixed(1)) : 0);
        });

        return {
            tooltip: {
                trigger: 'axis',
                formatter: (params: any) => {
                    const index = params?.[0]?.dataIndex || 0;
                    return [
                        categories[index] || '',
                        `Engaged rate: ${engagedRates[index] || 0}% (${(engagedCounts[index] || 0).toLocaleString()} sessions)`,
                        `Low engagement rate: ${lowRates[index] || 0}% (${(lowCounts[index] || 0).toLocaleString()} sessions)`,
                        `Total sessions: ${(totals[index] || 0).toLocaleString()}`,
                    ].join('<br/>');
                },
            },
            legend: { data: ['Engaged rate', 'Low engagement rate'], bottom: 0 },
            grid: { left: '6%', right: '6%', top: '10%', bottom: 70, containLabel: true },
            xAxis: { type: 'category', data: categories, axisLabel: { rotate: period === 'daily' ? 45 : 0 } },
            yAxis: { type: 'value', name: 'Rate', min: 0, max: 100, axisLabel: { formatter: '{value}%' } },
            series: [
                {
                    name: 'Engaged rate',
                    type: 'line',
                    data: engagedRates,
                    smooth: true,
                    itemStyle: { color: '#52c41a' },
                    lineStyle: { color: '#52c41a', width: 2 },
                    symbol: 'circle',
                    symbolSize: 6,
                },
                {
                    name: 'Low engagement rate',
                    type: 'line',
                    data: lowRates,
                    smooth: true,
                    itemStyle: { color: '#faad14' },
                    lineStyle: { color: '#faad14', width: 2 },
                    symbol: 'circle',
                    symbolSize: 6,
                },
            ],
        };
    }, [data, period]);

    return <ChartRenderer option={option} height={340} />;
};

export const LandingPageQualityChart: React.FC<{ data?: LandingPageQualityItem[] }> = ({ data = [] }) => {
    const option = React.useMemo(() => {
        const rows = [...data].reverse();
        const labels = rows.map(item => compactLabel(item.page_label || item.page_path));
        const fullLabels = rows.map(item => item.page_label || item.page_path || 'Unknown');
        const rates = rows.map(item => Number(item.engagement_rate_percent) || 0);

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const index = params?.[0]?.dataIndex || 0;
                    const row = rows[index] || {};
                    return [
                        fullLabels[index],
                        `Engagement rate: ${rates[index]}%`,
                        `Sessions: ${(Number(row.sessions) || 0).toLocaleString()}`,
                        `Engaged: ${(Number(row.engaged_sessions) || 0).toLocaleString()}`,
                        `Low engagement: ${(Number(row.low_engagement_sessions) || 0).toLocaleString()}`,
                    ].join('<br/>');
                },
            },
            grid: { left: '28%', right: '8%', top: '8%', bottom: '10%', containLabel: true },
            xAxis: { type: 'value', min: 0, max: 100, axisLabel: { formatter: '{value}%' } },
            yAxis: { type: 'category', data: labels },
            series: [
                {
                    name: 'Engagement rate',
                    type: 'bar',
                    data: rates,
                    itemStyle: { color: '#52c41a' },
                    barMaxWidth: 24,
                    label: { show: true, position: 'right', formatter: '{c}%', fontSize: 11 },
                },
            ],
        };
    }, [data]);

    return <ChartRenderer option={option} height={360} />;
};

export const TrafficSourceQualityChart: React.FC<{ data?: TrafficSourceQualityItem[] }> = ({ data = [] }) => {
    const option = React.useMemo(() => {
        const labels = data.map(item => compactLabel(item.source_label, 18));
        const fullLabels = data.map(item => item.source_label || 'Unknown');
        const engaged = data.map(item => Number(item.engaged_sessions) || 0);
        const low = data.map(item => Number(item.low_engagement_sessions) || 0);

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const index = params?.[0]?.dataIndex || 0;
                    const row = data[index] || {};
                    return [
                        fullLabels[index],
                        `Engaged sessions: ${(engaged[index] || 0).toLocaleString()}`,
                        `Low engagement: ${(low[index] || 0).toLocaleString()}`,
                        `Engagement rate: ${Number(row.engagement_rate_percent) || 0}%`,
                        `Total sessions: ${(Number(row.sessions) || 0).toLocaleString()}`,
                    ].join('<br/>');
                },
            },
            legend: { data: ['Engaged sessions', 'Low engagement'], bottom: 0 },
            grid: { left: '6%', right: '6%', top: '10%', bottom: 84, containLabel: true },
            xAxis: { type: 'category', data: labels, axisLabel: { rotate: labels.length > 4 ? 35 : 0 } },
            yAxis: { type: 'value', name: 'Sessions' },
            series: [
                { name: 'Engaged sessions', type: 'bar', data: engaged, itemStyle: { color: '#52c41a' }, barMaxWidth: 26 },
                { name: 'Low engagement', type: 'bar', data: low, itemStyle: { color: '#faad14' }, barMaxWidth: 26 },
            ],
        };
    }, [data]);

    return <ChartRenderer option={option} height={360} />;
};

export const DurationBucketsChart: React.FC<{ data?: DurationBucketItem[] }> = ({ data = [] }) => {
    const option = React.useMemo(() => {
        const labels = data.map(item => item.duration_bucket || 'Unknown');
        const engaged = data.map(item => Number(item.engaged_sessions) || 0);
        const low = data.map(item => Number(item.low_engagement_sessions) || 0);

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const index = params?.[0]?.dataIndex || 0;
                    const row = data[index] || {};
                    return [
                        labels[index],
                        `Engaged sessions: ${(engaged[index] || 0).toLocaleString()}`,
                        `Low engagement: ${(low[index] || 0).toLocaleString()}`,
                        `Engagement rate: ${Number(row.engagement_rate_percent) || 0}%`,
                        `Total sessions: ${(Number(row.sessions) || 0).toLocaleString()}`,
                    ].join('<br/>');
                },
            },
            legend: { data: ['Engaged sessions', 'Low engagement'], bottom: 0 },
            grid: { left: '6%', right: '6%', top: '10%', bottom: 70, containLabel: true },
            xAxis: { type: 'category', data: labels },
            yAxis: { type: 'value', name: 'Sessions' },
            series: [
                { name: 'Engaged sessions', type: 'bar', stack: 'sessions', data: engaged, itemStyle: { color: '#52c41a' }, barMaxWidth: 42 },
                { name: 'Low engagement', type: 'bar', stack: 'sessions', data: low, itemStyle: { color: '#faad14' }, barMaxWidth: 42 },
            ],
        };
    }, [data]);

    return <ChartRenderer option={option} height={340} />;
};

export const EngagementHeatmapChart: React.FC<{ data?: HeatmapItem[] }> = ({ data = [] }) => {
    const option = React.useMemo(() => {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const hours = Array.from({ length: 24 }, (_, hour) => `${hour}:00`);
        const rowByKey = new Map<string, HeatmapItem>();

        data.forEach(item => {
            rowByKey.set(`${Number(item.hour_of_day) || 0}-${Number(item.day_of_week) || 0}`, item);
        });

        const heatmapData = days.flatMap((_, dayIndex) => (
            hours.map((__, hourIndex) => {
                const row = rowByKey.get(`${hourIndex}-${dayIndex}`);
                return [hourIndex, dayIndex, Number(row?.engagement_rate_percent) || 0];
            })
        ));

        return {
            tooltip: {
                position: 'top',
                formatter: (params: any) => {
                    const [hourIndex, dayIndex, rate] = params.data;
                    const row = rowByKey.get(`${hourIndex}-${dayIndex}`) || {};
                    return [
                        `${days[dayIndex]} ${hours[hourIndex]}`,
                        `Engagement rate: ${rate}%`,
                        `Sessions: ${(Number(row.sessions) || 0).toLocaleString()}`,
                        `Engaged sessions: ${(Number(row.engaged_sessions) || 0).toLocaleString()}`,
                    ].join('<br/>');
                },
            },
            grid: { left: '8%', right: '4%', top: '8%', bottom: 86, containLabel: true },
            xAxis: { type: 'category', data: hours, splitArea: { show: true }, axisLabel: { interval: 2 } },
            yAxis: { type: 'category', data: days, splitArea: { show: true } },
            visualMap: {
                min: 0,
                max: 100,
                calculable: true,
                orient: 'horizontal',
                left: 'center',
                bottom: 10,
                formatter: (value: number) => `${value}%`,
            },
            series: [
                {
                    name: 'Engagement rate',
                    type: 'heatmap',
                    data: heatmapData,
                    label: { show: false },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 8,
                            shadowColor: 'rgba(0, 0, 0, 0.25)',
                        },
                    },
                },
            ],
        };
    }, [data]);

    return <ChartRenderer option={option} height={360} />;
};
